import { Button, ListItem } from '@material-ui/core'
import {
  StyledTaskAssignmentTitle,
  StyledTaskAssignmentSection,
} from 'components/common/Modals/styles'
import {
  AssignDataType,
  TaskAssignmentSectionProps,
} from 'components/common/TaskAssignmentSection/TaskAssignmentSection.types'
import {
  ASSIGNEE_TYPE_USER,
  ASSIGNEE_TYPE_USER_GROUP,
  ASSIGNED_USERS,
  ASSIGNED_USER_GROUPS,
  SELECT_ACTION_ADD,
} from 'constants/index'
import { filterArrBySearchQuery } from 'helpers/common'
import { Map } from 'immutable'
import React, { useState, useRef, useEffect, useMemo } from 'react'
import { injectIntl, WrappedComponentProps } from 'react-intl'
import { connect } from 'react-redux'
import { ReactComponent as Vector } from 'assets/img/select-vector.svg'
import {
  AssignmentDataItem,
  AssignmentTaskType,
} from 'routes/App/Apps/JobsTriggers/TriggerSchedule/TriggersSchedule.types'
import { User, UserGroup } from 'types/common'
import messages from './TaskAssignmentSection.messages'
import UsersListComponent from '../UsersList/UsersList'
import UsersListDropdown from '../UsersListDropdown/UsersListDropdown'
import UsersSelectList from '../UsersSelectList/UsersSelectList'
import { UsersFilterButton } from '../SelectedOtherFiltersDropdown/SelectedOtherFiltersDropdown.styles'
import { MenuTitle } from '../Menu'

export const TaskAssignmentSection = ({
  intl: { formatMessage },
  assignData,
  assignedUsers,
  assignedUserGroups,
  title,
  id,
  siteId,
  formData,
  setFormData,
  fieldName,
}: TaskAssignmentSectionProps & WrappedComponentProps) => {
  const assignedUsersAndGroups = useMemo(
    () => [...assignedUsers, ...assignedUserGroups],
    [JSON.stringify(assignedUsers), JSON.stringify(assignedUserGroups)],
  )

  const usersList = useMemo(
    () => [
      ...assignData.toJS().userList.map((user: User) => ({
        ...user,
        assigneeType: ASSIGNEE_TYPE_USER,
      })),
    ],
    [JSON.stringify(assignData)],
  )

  const usersGroupsList = useMemo(
    () => [
      ...assignData.toJS().groupList.map((userGroup: UserGroup) => ({
        ...userGroup,
        assigneeType: ASSIGNEE_TYPE_USER_GROUP,
      })),
    ],
    [JSON.stringify(assignData)],
  )

  const [
    assignmentDropdownAnchor,
    setAssignmentDropdownAnchor,
  ] = useState<HTMLElement | null>(null)
  const [assignmentSearchQuery, setAssignmentSearchQuery] = useState('')
  const [tmpUsers, setTmpUsers] = useState<User[]>(assignedUsers)
  const [tmpUserGroups, setTmpUserGroups] = useState<UserGroup[]>(
    assignedUserGroups,
  )
  const [filteredUsersList, setFilteredUsersList] = useState<User[]>(usersList)
  const [filteredUsersGroupsList, setFilteredUsersGroupsList] = useState<
    UserGroup[]
  >(usersGroupsList)
  const assignmentSearchFieldRef = useRef<HTMLElement>(null)

  useEffect(() => {
    const filteredUsers = filterArrBySearchQuery(
      usersList,
      assignmentSearchQuery,
    )
    const filteredUserGroups = filterArrBySearchQuery(
      usersGroupsList,
      assignmentSearchQuery,
    )

    setFilteredUsersList(filteredUsers)
    setFilteredUsersGroupsList(filteredUserGroups)
  }, [assignmentSearchQuery])

  const onCloseAssignmentMenu = () => {
    setAssignmentDropdownAnchor(null)
    setAssignmentSearchQuery('')
  }

  const updateAssignment = (
    item: User | UserGroup,
    singleTask: AssignmentTaskType,
    newTasks: AssignmentTaskType[],
    selectAction?: string,
  ) => {
    const assignmentType =
      item.assigneeType === ASSIGNEE_TYPE_USER
        ? ASSIGNED_USERS
        : ASSIGNED_USER_GROUPS

    const newAssignees =
      selectAction === SELECT_ACTION_ADD
        ? [...singleTask[assignmentType], item]
        : [...singleTask[assignmentType]].filter(
            ({ id: assigneeId }) => assigneeId !== item.id,
          )

    newTasks.push({
      ...singleTask,
      [assignmentType]: newAssignees,
    })
  }

  const onChangeAssignment = (
    item: User | UserGroup,
    taskId: number,
    siteId: number,
    assigneeType?: string,
    selectAction?: string,
  ) => {
    const newTriggersAssignmentData: AssignmentDataItem[] = []

    const field = formData[
      fieldName as keyof typeof formData
    ] as AssignmentDataItem

    if (Array.isArray(field)) {
      field.forEach(singleSite => {
        if (singleSite.site.id === siteId) {
          const newTasks: AssignmentTaskType[] = []

          singleSite.tasks.forEach((singleTask: AssignmentTaskType) => {
            if (singleTask.id === taskId) {
              updateAssignment(item, singleTask, newTasks, selectAction)
            } else {
              newTasks.push(singleTask)
            }
          })

          newTriggersAssignmentData.push({
            ...singleSite,
            tasks: [...newTasks],
          })
        } else {
          newTriggersAssignmentData.push(singleSite)
        }
      })

      setFormData({
        ...formData,
        [fieldName]: newTriggersAssignmentData,
      })
    }
  }

  const renderFilteredAssignmentList = () => (
    <div>
      {!!filteredUsersList.length && (
        <>
          <MenuTitle>{formatMessage(messages.users)}</MenuTitle>
          <UsersSelectList
            list={filteredUsersList}
            tmpUsersArr={tmpUsers}
            setTmpUsersArr={setTmpUsers}
            extraAction={(item, assigneeType, selectAction) => {
              onChangeAssignment(item, id, siteId, assigneeType, selectAction)
            }}
          />
        </>
      )}
      {!!filteredUsersGroupsList.length && (
        <>
          <MenuTitle>{formatMessage(messages.userGroups)}</MenuTitle>
          <UsersSelectList
            list={filteredUsersGroupsList}
            tmpUsersArr={tmpUserGroups}
            setTmpUsersArr={setTmpUserGroups}
            extraAction={(item, assigneeType, selectAction) => {
              onChangeAssignment(item, id, siteId, assigneeType, selectAction)
            }}
          />
        </>
      )}
      {!filteredUsersList.length && !filteredUsersGroupsList.length && (
        <ListItem>{formatMessage(messages.noResultsMessage)}</ListItem>
      )}
    </div>
  )

  return (
    <StyledTaskAssignmentSection align="center">
      <StyledTaskAssignmentTitle>{title}</StyledTaskAssignmentTitle>
      <UsersFilterButton isMenuOpen={!!assignmentDropdownAnchor}>
        <Button
          onClick={({ currentTarget }) => {
            setAssignmentDropdownAnchor(currentTarget)
            setTimeout(() => {
              if (assignmentSearchFieldRef.current) {
                assignmentSearchFieldRef.current.focus()
              }
            }, 0)
          }}
          endIcon={<Vector />}>
          <UsersListComponent
            hideNoAssigneesIcon
            users={assignedUsersAndGroups}
          />
        </Button>
      </UsersFilterButton>
      <UsersListDropdown
        anchor={assignmentDropdownAnchor}
        onCloseMenu={onCloseAssignmentMenu}
        searchQuery={assignmentSearchQuery}
        setSearchQuery={setAssignmentSearchQuery}
        inputRef={assignmentSearchFieldRef}
        renderUserList={renderFilteredAssignmentList}
      />
    </StyledTaskAssignmentSection>
  )
}

export default connect(
  (state: { getIn: (path: Array<string>) => Map<string, AssignDataType> }) => ({
    assignData: state.getIn(['myJobs', 'assignData']),
  }),
)(injectIntl(TaskAssignmentSection))
